/**
 * SignupCredentialsForm
 *
 * Presentation-only credentials form (firstName, lastName, email, password,
 * terms checkbox). Extracted from SMBSignupForm so multiple flows can share
 * the exact same visual:
 *   - SMBSignupForm — the legacy "Get Free Access" gate (with Browse-free
 *     subtitle and "Already have an account? Sign in" footer rendered by
 *     the parent).
 *   - SelfServiceSMBSignupModal/AccountStep — ENG-5201's phoneless self-
 *     service signup (no footer; advances to email-otp step on success).
 *
 * Pure presentation: owns form JSX + useStyles + validation wiring. The
 * caller provides the submit handler (returns a ValidationErrors object to
 * surface server-side field errors via React Final Form) and the button
 * labels. No analytics, no recaptcha, no redirect logic.
 *
 * ENG-5251: the label reads "Work email" as a soft nudge. The work-email
 * check itself is post-signup — see SetupPausedBannerSurface for the
 * modular "Have a work email?" component that handles the add-and-promote
 * flow once the user lands on the SMB-incomplete dashboard.
 */
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import { Field, Form } from 'react-final-form';

import { PasswordText } from '@/auth/components/PasswordTextField';
import Button from '@/components/Button';
import { Checkbox, TextField } from '@/components/FormAdapters';
import Link from '@/components/Link';
import { policies } from '@/core/agreements';
import { normalizeSpace } from '@/utils';

import { SMBSignupFormData } from './types';
import { ValidationErrors, validateSMBSignupForm } from './utils/validation';

const TOU_POLICY = policies['terms-of-use'];
const PRIVACY_POLICY = policies.privacy;

// makeStyles uses literal px values rather than theme.spacing/theme.palette
// so the form renders correctly outside an MUI ThemeProvider (e.g. unit
// tests). Spacing matches the original SMBSignupForm — `theme.spacing(N)`
// expands to `N * 8px` against the default MUI theme.
const useStyles = makeStyles(() => ({
  agreements: {
    marginTop: 2, // Reduced gap from password field (2px ≈ 0.25 spacing units)
    marginBottom: 12, // 12px ≈ 1.5 spacing units
    marginLeft: '10px', // 10px to the right
  },
  checkbox: {
    padding: '0 6px',
    '& .MuiSvgIcon-root': {
      fontSize: 18, // Smaller checkbox icon (default is 24)
    },
  },
  checkboxLabel: {
    fontSize: '10px !important', // 1px smaller
    lineHeight: '1.3 !important',
    '& span': {
      fontSize: '12px !important',
    },
    '& a': {
      fontSize: '12px !important',
    },
  },
  button: {
    padding: '8px 16px', // Thinner button
    minHeight: 36, // Reduced button height
  },
  field: {
    marginBottom: '12px', // Comfortable spacing between fields
    position: 'relative',
    '& .MuiOutlinedInput-root': {
      fontSize: 14, // Slightly bigger input text
      '& input': {
        padding: '12px 12px', // A bit taller - increased from 10px
        fontSize: 14, // Slightly bigger input font
      },
      '& .MuiIconButton-root': {
        padding: 6, // Smaller icon button padding
        '& .MuiSvgIcon-root': {
          fontSize: 18, // Smaller icons (eye and question mark)
        },
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: 13, // Smaller label font
      transform: 'translate(14px, 12px) scale(1)', // Center label vertically when inside
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)', // Floating label position
      },
    },
    '& .MuiInputLabel-asterisk': {
      display: 'none', // Hide asterisk
    },
    '& .MuiFormHelperText-root': {
      position: 'absolute',
      right: 12,
      top: '50%',
      transform: 'translateY(-50%)',
      margin: 0,
      fontSize: 11,
      color: '#d32f2f', // Matches MUI default theme.palette.error.main
      backgroundColor: 'white',
      padding: '0 4px',
      pointerEvents: 'none',
    },
  },
}));

export interface SignupCredentialsFormProps {
  // Async submit handler. Resolves with React Final Form ValidationErrors to
  // attach server-side field errors (e.g., {email: 'Already in use'}); returns
  // void on success. Throws are caught and surfaced via the error prop only
  // if the caller maps them.
  onSubmit: (values: SMBSignupFormData) => Promise<ValidationErrors | void>;
  // External loading flag (e.g., recaptcha + signup mutation in flight).
  // Combined with React Final Form's submitting state to disable the button.
  loading?: boolean;
  // Banner-style error rendered above the submit button. Pass null to hide.
  error?: string | null;
  submitLabel: string;
  submittingLabel: string;
  // Optional analytics hooks for the policy links. No-op by default.
  onPrivacyClick?: () => void;
  onTermsClick?: () => void;
}

export function SignupCredentialsForm({
  onSubmit,
  loading = false,
  error = null,
  submitLabel,
  submittingLabel,
  onPrivacyClick,
  onTermsClick,
}: SignupCredentialsFormProps): JSX.Element {
  const s = useStyles();

  return (
    <Form<SMBSignupFormData>
      onSubmit={onSubmit}
      validate={validateSMBSignupForm}
      subscription={{
        submitting: true,
        hasValidationErrors: true,
        dirtySinceLastSubmit: true,
        hasSubmitErrors: true,
      }}
    >
      {({
        handleSubmit,
        submitting,
        hasValidationErrors,
        dirtySinceLastSubmit,
        hasSubmitErrors,
      }) => (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={0.5}>
            <Grid item xs={12} sm={6}>
              <Field
                component={TextField}
                name="first_name"
                label="First Name"
                variant="outlined"
                required
                margin="none"
                changeOnBlur={false}
                inputProps={{ maxLength: 255 }}
                format={normalizeSpace}
                className={s.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                component={TextField}
                name="last_name"
                label="Last Name"
                variant="outlined"
                required
                margin="none"
                changeOnBlur={false}
                inputProps={{ maxLength: 255 }}
                format={normalizeSpace}
                className={s.field}
              />
            </Grid>
          </Grid>
          <Field
            component={TextField}
            changeOnBlur={false}
            name="email"
            label="Work email"
            variant="outlined"
            inputProps={{ autoComplete: 'off' }}
            required
            margin="none"
            className={s.field}
          />
          <Field
            component={PasswordText}
            name="password"
            label="Password"
            variant="outlined"
            showHelp
            inputProps={{ autoComplete: 'new-password' }}
            required
            margin="none"
            className={s.field}
          />

          <div className={s.agreements}>
            <Field
              type="checkbox"
              component={Checkbox}
              classes={{ root: s.checkbox }}
              labelClasses={{ root: s.checkboxLabel }}
              FormControlProps={{ margin: 'none' }}
              name="accept_privacy_and_terms_of_use"
              label={
                <span>
                  I have read and agree to OnFrontiers{' '}
                  <Link newTab href={PRIVACY_POLICY.url} onClick={onPrivacyClick}>
                    {PRIVACY_POLICY.name}
                  </Link>{' '}
                  &amp;{' '}
                  <Link newTab href={TOU_POLICY.url} onClick={onTermsClick}>
                    {TOU_POLICY.name}
                  </Link>
                </span>
              }
            />
          </div>

          {error && <Box sx={{ color: 'error.main', mb: 2 }}>{error}</Box>}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={
              hasValidationErrors ||
              (hasSubmitErrors && !dirtySinceLastSubmit) ||
              submitting ||
              loading
            }
            classes={{ root: s.button }}
          >
            {submitting || loading ? submittingLabel : submitLabel}
          </Button>
        </form>
      )}
    </Form>
  );
}
